import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Container, Row, Col, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import Flatpickr from 'react-flatpickr';
import 'react-toastify/dist/ReactToastify.css';
import classnames from 'classnames';

import {
  getPlayerMetrics,
  getFinancialMetrics,
  getRetentionCohorts,
  getConversionFunnel,
} from '@/services/analytics/analytics-service';
import { AnalyticsQueryDto, QueryPeriods } from '@/services/analytics/analytics-types';
import { getDateMinusDays } from '@/common/query/QueryPeriods';
import { showToast } from '@/common/showToast';

// Components
import AnalyticsSummarySection from './components/AnalyticsSummarySection';
import PlayerMetricsSection from './components/PlayerMetricsSection';
import FinancialMetricsSection from './components/FinancialMetricsSection';
import RetentionCohortsSection from './components/RetentionCohortsSection';
import ConversionFunnelSection from './components/ConversionFunnelSection';

const AnalyticsBeta = () => {
  const today = new Date().toISOString().split('T')[0];
  const [activeTab, setActiveTab] = useState('1');
  const [days, setDays] = useState<number>(30);
  const [toDate, setToDate] = useState(today);
  const [fromDate, setFromDate] = useState(() => getDateMinusDays(days));

  const [queryParams, setQueryParams] = useState<AnalyticsQueryDto>({
    fromDate: fromDate,
    toDate: toDate,
    period: QueryPeriods.day,
    groupByDate: true,
  });

  const setRange = (range: number) => () => {
    setDays(range);
    setFromDate(getDateMinusDays(range));
    setToDate(today);
  };

  const handleDateChange = (dates: Date[]) => {
    if (dates.length === 2) {
      const [start, end] = dates.map((date) => date.toISOString().split('T')[0]);
      setFromDate(start);
      setToDate(end);
    }
  };

  useEffect(() => {
    const differenceInDays =
      Math.abs(new Date(toDate).getTime() - new Date(fromDate).getTime()) / (24 * 60 * 60 * 1000);
    if (differenceInDays < 0) return showToast('Invalid date range', 'warning');

    setDays(differenceInDays);

    setQueryParams((prevState) => ({
      ...prevState,
      fromDate,
      toDate,
    }));
  }, [fromDate, toDate]);

  // Queries
  const { data: playerMetrics, isLoading: playerMetricsLoading } = useQuery({
    queryKey: ['playerMetrics', queryParams],
    queryFn: ({ queryKey }) => {
      const [, params] = queryKey as [string, AnalyticsQueryDto];
      return getPlayerMetrics(params);
    },
  });

  const { data: financialMetrics, isLoading: financialMetricsLoading } = useQuery({
    queryKey: ['financialMetrics', queryParams],
    queryFn: ({ queryKey }) => {
      const [, params] = queryKey as [string, AnalyticsQueryDto];
      return getFinancialMetrics(params);
    },
  });

  const { data: retentionCohorts, isLoading: retentionCohortsLoading } = useQuery({
    queryKey: ['retentionCohorts', queryParams],
    queryFn: ({ queryKey }) => {
      const [, params] = queryKey as [string, AnalyticsQueryDto];
      return getRetentionCohorts(params);
    },
  });

  const { data: conversionFunnel, isLoading: conversionFunnelLoading } = useQuery({
    queryKey: ['conversionFunnel', queryParams],
    queryFn: ({ queryKey }) => {
      const [, params] = queryKey as [string, AnalyticsQueryDto];
      return getConversionFunnel(params);
    },
  });

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid className="container-fluid">
          <Row className="mb-3">
            <Col>
              <h4 className="page-title">Analytics Dashboard (Beta)</h4>
              <p className="text-muted">Enhanced analytics with improved metrics and insights</p>
            </Col>
          </Row>

          {/* Analytics Summary - Always visible */}
          <AnalyticsSummarySection />

          {/* Date Range Selector */}
          <Row className="g-3 d-flex justify-content-end mb-4">
            <div className="col-sm-auto">
              <div className="input-group">
                <Flatpickr
                  className="form-control dash-filter-picker border-0 shadow"
                  options={{
                    mode: 'range',
                    dateFormat: 'Y-m-d',
                    defaultDate: [fromDate, toDate],
                  }}
                  onChange={handleDateChange}
                />
                <div className="input-group-text bg-primary border-primary text-white">
                  <i className="ri-calendar-2-line"></i>
                </div>
              </div>
            </div>
            <div className="d-flex flex-rows justify-content-end gap-2">
              <button className={'btn' + (days === 7 ? ' btn-primary' : ' btn-dark')} onClick={setRange(7)}>
                7d
              </button>
              <button className={'btn' + (days === 30 ? ' btn-primary' : ' btn-dark')} onClick={setRange(30)}>
                30d
              </button>
              <button className={'btn' + (days === 60 ? ' btn-primary' : ' btn-dark')} onClick={setRange(60)}>
                60d
              </button>
              <button className={'btn' + (days === 90 ? ' btn-primary' : ' btn-dark')} onClick={setRange(90)}>
                90d
              </button>
            </div>
          </Row>

          {/* Tabs Navigation */}
          <Nav tabs className="nav-tabs-custom mb-3">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === '1' })}
                onClick={() => toggleTab('1')}
                style={{ cursor: 'pointer' }}
              >
                <i className="bx bx-user-circle me-2"></i>
                Player & Growth
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === '2' })}
                onClick={() => toggleTab('2')}
                style={{ cursor: 'pointer' }}
              >
                <i className="bx bx-dollar-circle me-2"></i>
                Financial
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === '3' })}
                onClick={() => toggleTab('3')}
                style={{ cursor: 'pointer' }}
              >
                <i className="bx bx-trending-up me-2"></i>
                Retention & Cohorts
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === '4' })}
                onClick={() => toggleTab('4')}
                style={{ cursor: 'pointer' }}
              >
                <i className="bx bx-filter-alt me-2"></i>
                Conversion Funnel
              </NavLink>
            </NavItem>
          </Nav>

          {/* Tab Content */}
          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <PlayerMetricsSection data={playerMetrics} isLoading={playerMetricsLoading} />
            </TabPane>
            <TabPane tabId="2">
              <FinancialMetricsSection data={financialMetrics} isLoading={financialMetricsLoading} />
            </TabPane>
            <TabPane tabId="3">
              <RetentionCohortsSection data={retentionCohorts} isLoading={retentionCohortsLoading} />
            </TabPane>
            <TabPane tabId="4">
              <ConversionFunnelSection data={conversionFunnel} isLoading={conversionFunnelLoading} />
            </TabPane>
          </TabContent>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AnalyticsBeta;
